import { ChangeEvent, useMemo, useState } from 'react';

import { paginationListLimit } from '../constants/systemConstants';

export type UsePaginationReturn<TItem> = {
  paginatedData: TItem[];
  page: number;
  totalPages: number;
  /** Matches LbPagination onChange signature */
  handlePageChange: (event: ChangeEvent<unknown>, page: number) => void;
  /** Call when filters/search change so the user isn't left on a no-longer-existing page */
  resetPage: () => void;
};

export const usePagination = <TItem>(
  data: TItem[],
  elementsPerPage: number = paginationListLimit,
): UsePaginationReturn<TItem> => {
  const [page, setPage] = useState(1);

  const totalPages = Math.ceil(data.length / elementsPerPage);
  // Keep slice in range when data shrinks (e.g. filters) without an explicit resetPage
  const currentPage = totalPages === 0 ? 1 : Math.min(page, totalPages);

  const paginatedData = useMemo(
    () => data.slice((currentPage - 1) * elementsPerPage, currentPage * elementsPerPage),
    [data, currentPage, elementsPerPage],
  );

  const handlePageChange = (event: ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  const resetPage = () => {
    setPage(1);
  };

  return { paginatedData, page: currentPage, totalPages, handlePageChange, resetPage };
};
